import React from "react";
import { Button } from "@/components/ui/button";

/**
 * Top-level error boundary so a runtime error never produces a fully-blank
 * screen. Shows the error message + stack and a "Reload" button. Also logs
 * the error to console.error for any external error tracking.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
    this.setState({ info });
  }

  handleReload = () => {
    this.setState({ error: null, info: null });
    window.location.reload();
  };

  handleHome = () => {
    this.setState({ error: null, info: null });
    window.location.href = "/";
  };

  render() {
    if (!this.state.error) return this.props.children;

    const message = String(this.state.error?.message || this.state.error || "Unknown error");
    const stack = this.state.error?.stack || "";

    return (
      <div className="min-h-screen bg-bone p-6 sm:p-12">
        <div className="max-w-2xl mx-auto hs-card p-8" data-testid="error-boundary">
          <div className="hs-label text-error">Something went wrong</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2">
            We hit a snag rendering this page.
          </h1>
          <p className="mt-3 text-ink-2">
            The app caught an unexpected error so you don't see a blank screen. Try reloading; if it
            keeps happening, share the details below with your coach (or with support).
          </p>
          <div className="mt-5 flex gap-3 flex-wrap">
            <Button onClick={this.handleReload} className="rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white" data-testid="error-boundary-reload">
              Reload
            </Button>
            <Button variant="secondary" onClick={this.handleHome} className="rounded-xl" data-testid="error-boundary-home">
              Go home
            </Button>
          </div>
          <div className="mt-8 p-4 rounded-xl bg-muted2 font-data text-xs text-ink-2 overflow-x-auto">
            <div className="hs-label text-[10px] text-ink-3 mb-2">Error details</div>
            <div className="font-bold text-error mb-2 break-words">{message}</div>
            <pre className="whitespace-pre-wrap break-words leading-relaxed">{stack}</pre>
          </div>
        </div>
      </div>
    );
  }
}
